import { browserFetch } from '../../services/browserSession';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Trophy,
  CircleDollarSign,
  CheckCircle2,
  Clock3,
  Eye,
  BarChart3,
  CalendarDays,
  Sparkles,
  Plus,
  Mail,
  Check,
  X
} from 'lucide-react';
import { toast } from 'react-toastify';
import { getCreatorInvitations, respondToInvitation } from '../../services/api';
import { Button, Card, Badge, CollabTypeBadge } from '../common/design-system';
import styles from './MyApplications.module.scss';
import clsx from 'clsx';
import Avatar from '../common/Avatar';
import ImageWithFallback from '../common/ImageWithFallback';

const PAGE_POLL_INTERVAL_MS = 30000;

const getApplicationTimestamp = (value) => {
  const timestamp = Date.parse(value || '');
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const sortApplicationsByRecentActivity = (a, b) => (
  getApplicationTimestamp(b.updated_at) - getApplicationTimestamp(a.updated_at) ||
  getApplicationTimestamp(b.created_at) - getApplicationTimestamp(a.created_at)
);

const formatApplicationDate = (value, language) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(language || 'en', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatApplicationDateRange = (start, end, language) => {
  const startLabel = formatApplicationDate(start, language);
  const endLabel = formatApplicationDate(end, language);
  if (startLabel && endLabel) return `${startLabel} – ${endLabel}`;
  return startLabel || endLabel;
};

// ─── Stat Item Component ──────────────────────────────────
const OverviewStat = ({ icon: Icon, label, value, helper, accent = false }) => (
  <div className={styles['stat-card-modern']}>
    <div className={styles['stat-content']}>
      <div className={clsx(styles['icon-box'], accent && styles.accent)}>
        <Icon size={24} />
      </div>
      <div className={styles['stat-text-box']}>
        <span className={styles['stat-label']}>{label}</span>
        <strong className={styles['stat-value']}>{value}</strong>
        <span className={styles['stat-helper']}>{helper}</span>
      </div>
    </div>
  </div>
);

// ─── Application Card Component ───────────────────────────
const ApplicationItem = ({ app }) => {
  const { t, i18n } = useTranslation('creator');
  const navigate = useNavigate();

  const statusConfig = useMemo(() => {
    const configs = {
       pending: { label: t('applications.card.status.pending'), tone: 'warning' },
       in_progress: { label: t('applications.card.status.in_progress'), tone: 'progress' },
       completed: { label: t('applications.card.status.completed'), tone: 'completed' },
       rejected: { label: t('applications.card.status.rejected'), tone: 'error' },
       winner: { label: t('applications.card.status.winner'), tone: 'success' }
    };
    return configs[app.status] || configs.pending;
  }, [app.status, t]);

  const activityDate = formatApplicationDate(app.updated_at || app.created_at, i18n.resolvedLanguage || i18n.language);
  const scheduleLabel = formatApplicationDateRange(
    app.start_date,
    app.end_date,
    i18n.resolvedLanguage || i18n.language,
  );
  const compensationValue = app.cashEarnings > 0
    ? `$ ${app.cashEarnings.toLocaleString()}`
    : app.exchangeValue > 0
      ? `$ ${app.exchangeValue.toLocaleString()}`
      : null;

  const handleCardClick = (e) => {
    if (e.target.closest('button') || e.target.closest('a')) return;
    if (['in_progress', 'winner', 'completed'].includes(app.status)) {
        navigate(`/creator/collabs/${app.collab_id}`);
    } else {
        navigate(`/creator/offers/${app.collab_id}`);
    }
  };

  const handleCardKeyDown = (e) => {
    if (e.target.closest('button') || e.target.closest('a')) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick(e);
    }
  };

  return (
    <Card
      className={styles['app-card-editorial']}
      interactive
      role="link"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      aria-label={`${app.collab_title} — ${statusConfig.label}`}
    >
      <div className={styles['card-header-visual']}>
        <ImageWithFallback src={app.collab_image} alt={app.collab_title} name={app.collab_title} className={styles['cover-img']} />
        <div className={styles['header-overlay']}>
          <div className={styles['header-top-row']}>
            <div className={styles['brand-abbr']}>
              <Avatar size="xs" src={app.brand_logo} name={app.brand_name} alt={`${app.brand_name} logo`} />
            </div>
          </div>
        </div>
        <div className={styles['image-strip']}>
          <div className={styles['strip-item']}>
            <CalendarDays size={12} />
            <span>{scheduleLabel || t('applications.card.upcoming')}</span>
          </div>
          {compensationValue && (
            <div className={styles['strip-item']}>
              <CircleDollarSign size={12} />
              <span>{compensationValue}</span>
            </div>
          )}
        </div>
      </div>

      <div className={styles['card-body-mid']}>
        <div className={styles['card-status-row']}>
          <div className={styles['status-badge-group']}>
            <span className={clsx(styles['status-badge'], styles[statusConfig.tone])}>
              <span className={styles['status-dot']} />
              {statusConfig.label}
            </span>
            <CollabTypeBadge offer={app} size="sm" />
          </div>
          {activityDate && (
            <span className={styles['activity-meta']}>
              <Clock3 size={12} />
              {t('applications.card.updated_on', { date: activityDate })}
            </span>
          )}
        </div>
        <div className={styles['brand-tag']}>{app.brand_name}</div>
        <h3>{app.collab_title}</h3>
        <p>{app.description?.replace(/<[^>]*>/g, '').substring(0, 100) || t('applications.card.no_description')}</p>
        <div className={styles['meta-grid']}>
          <div className={styles['meta-pill']}>
            <Sparkles size={14} />
            <span>{app.category}</span>
          </div>
          {app.cashEarnings > 0 && (
            <div className={clsx(styles['meta-pill'], styles.gold)}>
              <CircleDollarSign size={14} />
              <span>$ {app.cashEarnings.toLocaleString()}</span>
            </div>
          )}
        </div>
      </div>

      <div className={styles['card-footer-actions']}>
        <Button variant="outline" size="md" fullWidth className={styles['card-action-button']} onClick={(e) => { e.stopPropagation(); navigate(`/creator/offers/${app.collab_id}`); }}>
          <Eye size={16} /> {t('applications.card.view_details')}
        </Button>
        {['in_progress', 'winner', 'completed'].includes(app.status) && (
          <Button variant="primary" size="md" fullWidth className={styles['card-action-button']} onClick={(e) => { e.stopPropagation(); navigate(`/creator/collabs/${app.collab_id}`); }}>
            <BarChart3 size={16} /> {app.status === 'completed' ? t('applications.card.view_metrics') : t('applications.card.execution_details')}
          </Button>
        )}
      </div>
    </Card>
  );
};

// ─── Invitation Card Component ────────────────────────────
 const InvitationItem = ({ inv, onRespond }) => {
   const { t, i18n } = useTranslation('creator');
   const navigate = useNavigate();
  const [responding, setResponding] = useState(false);

  const handleAction = async (action) => {
    setResponding(true);
    await onRespond(inv.id, action, inv.offer || inv.offer_details?.id);
    setResponding(false);
  };

  const offer = inv.offer_details || {};

  const handleCardClick = (e) => {
    if (e.target.closest('button') || e.target.closest('a')) return;
    navigate(`/creator/offers/${inv.offer}`);
  };

  const handleCardKeyDown = (e) => {
    if (e.target.closest('button') || e.target.closest('a')) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick(e);
    }
  };

  return (
    <Card
      className={clsx(styles['app-card-editorial'], styles['invitation-card'])}
      interactive
      role="link"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      aria-label={`${offer.title} — ${t('applications.card.direct_invitation')}`}
    >
      <div className={styles['card-header-visual']}>
        <ImageWithFallback src={offer.featured_image_url || offer.featured_image} alt={offer.title} name={offer.title} className={styles['cover-img']} />
        <div className={styles['header-overlay']}>
          <div className={styles['header-top-row']}>
            <Badge variant="warning" className={clsx(styles['status-badge'], styles['invitation-badge'])}>{t('applications.card.direct_invitation')}</Badge>
            <div className={styles['brand-abbr']}>
              <Avatar size="xs" src={offer.business_logo_url || offer.business_logo} name={offer.business_name} alt={`${offer.business_name} logo`} />
            </div>
          </div>
        </div>
        <div className={styles['image-strip']}>
          <div className={styles['strip-item']}>
            <CalendarDays size={12} />
            <span>{formatApplicationDateRange(
              offer.from_date,
              offer.to_date,
              i18n.resolvedLanguage || i18n.language,
            ) || t('applications.card.upcoming')}</span>
          </div>
          {(Number(offer.cash_compensation) > 0 || Number(offer.value) > 0) && (
            <div className={styles['strip-item']}>
              <CircleDollarSign size={12} />
              <span>{offer.cash_compensation > 0
                ? `${offer.cash_currency || 'USD'} ${Number(offer.cash_compensation).toLocaleString(i18n.language)}`
                : `$${Number(offer.value).toLocaleString()}`}</span>
            </div>
          )}
        </div>
      </div>

      <div className={styles['card-body-mid']}>
        <div className={styles['card-status-row']}>
          <div className={styles['status-badge-group']}>
            <CollabTypeBadge offer={offer} size="sm" />
          </div>
        </div>
        <div className={styles['brand-tag']}>{offer.business_name}</div>
        <h3>{offer.title}</h3>
        <p>{offer.description?.replace(/<[^>]*>/g, '').substring(0, 100) || t('applications.card.no_description')}</p>
        <div className={styles['meta-grid']}>
          <div className={styles['meta-pill']}>
            <Sparkles size={14} />
            <span>{offer.type_display}</span>
          </div>
        </div>
      </div>

      <div className={styles['card-footer-actions']}>
        <Button variant="outline" size="md" className={styles['card-action-button']} onClick={(e) => { e.stopPropagation(); handleAction('decline'); }} disabled={responding} style={{ flex: 1 }}>
          <X size={16} className="mr-2" /> {t('applications.card.decline')}
        </Button>
        <Button variant="primary" size="md" className={styles['card-action-button']} onClick={(e) => { e.stopPropagation(); handleAction('accept'); }} disabled={responding} style={{ flex: 1 }}>
          <Check size={16} className="mr-2" /> {t('applications.card.accept')}
        </Button>
      </div>
      <div className={styles['card-secondary-action']}>
        <Button variant="ghost" size="sm" fullWidth onClick={(e) => { e.stopPropagation(); navigate(`/creator/offers/${inv.offer}`); }}>
          {t('applications.card.view_offer_details')}
        </Button>
      </div>
    </Card>
  );
};

// ─── Main Component ───────────────────────────────────────
 const MyApplications = () => {
     const { t } = useTranslation('creator');
     const [applications, setApplications] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const applicationStatusRef = useRef({});
    const hasLoadedApplicationsRef = useRef(false);
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const requestedInitialTab = new URLSearchParams(location.search).get('tab');
    const initialTab = ['invitations', 'past'].includes(requestedInitialTab) ? requestedInitialTab : 'active';
    const [activeTab, setActiveTab] = useState(initialTab);
    const [searchTerm, setSearchTerm] = useState('');
    const fetchApplications = useCallback(async ({ silent = false } = {}) => {
        try {
            if (!silent) setLoading(true);

            const response = await browserFetch('/api/feed/my-applications/', {
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                const results = Array.isArray(data) ? data : data.results || [];

                const mapped = results.map(app => ({
                    id: app.id,
                    collab_id: app.offer,
                    collab_title: app.offer_details?.title || t('applications.defaults.collab'),
                    collab_image: app.offer_details?.featured_image_url || app.offer_details?.featured_image,
                    brand_name: app.offer_details?.business_name || t('applications.defaults.business'),
                    brand_logo: app.offer_details?.business_logo_url || app.offer_details?.business_logo || null,
                    status: app.status === 'accepted' ? 'in_progress' : (app.status === 'applied' ? 'pending' : app.status),
                    cashEarnings: Number(app.earnings || 0),
                    exchangeValue: Number(app.offer_details?.exchange_value ?? app.offer_details?.value ?? 0),
                    compensationType: app.offer_details?.compensation_type || 'exchange',
                    compensation_mode: app.offer_details?.compensation_mode,
                    sponsor_this_offer: app.offer_details?.sponsor_this_offer,
                    cash_compensation: app.offer_details?.cash_compensation,
                    sponsor_amount: app.offer_details?.sponsor_amount,
                    offer_details: app.offer_details,
                    start_date: app.offer_details?.from_date || app.offer_details?.start_date,
                    end_date: app.offer_details?.to_date || app.offer_details?.end_date,
                    category: app.offer_details?.type_display || t('applications.defaults.general'),
                    description: app.offer_details?.description,
                    created_at: app.created_at,
                    updated_at: app.updated_at
                }));
                if (hasLoadedApplicationsRef.current) {
                    mapped.forEach((app) => {
                        const previousStatus = applicationStatusRef.current[app.id];
                        if (previousStatus === 'pending' && app.status === 'in_progress') {
                            toast.success(t('applications.messages.accepted_update', { title: app.collab_title }));
                        }
                        if (previousStatus === 'pending' && app.status === 'rejected') {
                            toast.info(t('applications.messages.rejected_update', { title: app.collab_title }));
                        }
                    });
                }

                applicationStatusRef.current = mapped.reduce((acc, app) => {
                    acc[app.id] = app.status;
                    return acc;
                }, {});
                hasLoadedApplicationsRef.current = true;
                setApplications(mapped);
            }

            // Fetch invitations
            try {
                const invRes = await getCreatorInvitations();
                const invitationResults = Array.isArray(invRes.data) ? invRes.data : invRes.data.results || [];
                setInvitations(invitationResults);
            } catch (invErr) {
                console.error('Error fetching invitations:', invErr);
            }

        } catch (err) {
            console.error('Error loading collabs:', err);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchApplications();
    }, [fetchApplications]);

    useEffect(() => {
        const interval = window.setInterval(() => {
            if (!document.hidden) fetchApplications({ silent: true });
        }, PAGE_POLL_INTERVAL_MS);
        const refresh = () => {
            if (!document.hidden) fetchApplications({ silent: true });
        };
        const handleVisibleRefresh = () => {
            if (!document.hidden) refresh();
        };

        window.addEventListener('focus', refresh);
        window.addEventListener('foth:applications-refresh', refresh);
        document.addEventListener('visibilitychange', handleVisibleRefresh);

        return () => {
            window.clearInterval(interval);
            window.removeEventListener('focus', refresh);
            window.removeEventListener('foth:applications-refresh', refresh);
            document.removeEventListener('visibilitychange', handleVisibleRefresh);
        };
    }, [fetchApplications]);

    useEffect(() => {
        const requestedTab = new URLSearchParams(location.search).get('tab');
        if (requestedTab === 'invitations') setActiveTab('invitations');
        if (requestedTab === 'past') setActiveTab('past');
    }, [location.search]);

    const pendingInvitations = useMemo(
        () => invitations.filter(inv => inv.status === 'pending'),
        [invitations]
    );

    const filteredList = useMemo(() => {
        return applications.filter(app => {
            const isTabMatch = activeTab === 'active'
                ? !['completed', 'rejected', 'cancelled'].includes(app.status)
                : ['completed', 'rejected', 'cancelled'].includes(app.status);

            const isSearchMatch = app.brand_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                 app.collab_title.toLowerCase().includes(searchTerm.toLowerCase());

            return isTabMatch && isSearchMatch;
        }).sort(sortApplicationsByRecentActivity);
    }, [applications, activeTab, searchTerm]);

    const stats = useMemo(() => {
        const active = applications.filter(a => ['in_progress', 'pending', 'winner'].includes(a.status)).length;
        const completed = applications.filter(a => a.status === 'completed').length;
        const pendingInvites = pendingInvitations.length;
        return {
            active,
            completed,
            pendingInvites
        };
    }, [applications, pendingInvitations]);

    const handleInvitationResponse = async (id, action, offerId) => {
         try {
             const res = await respondToInvitation(id, action);
             toast.success(action === 'accept' ? t('applications.messages.invitation_accepted') : t('applications.messages.invitation_declined'));

            // Optimistic update: remove from invitations list immediately
            setInvitations(prev => prev.filter(inv => inv.id !== id));

            // If accepted, redirect to the accepted collab workspace
             if (action === 'accept') {
                 const targetOfferId = offerId || res.data?.invitation?.offer;
                 if (targetOfferId) {
                     navigate(`/creator/collabs/${targetOfferId}`);
                 } else {
                     fetchApplications();
                 }
             }
         } catch (error) {
             toast.error(t('applications.messages.invitation_error'));
         }
     };

     if (loading) return (
         <div className={styles['loading-sc']}>
             <div className={styles.spinner} />
             <p>{t('applications.loading')}</p>
         </div>
     );

    return (
        <div className={styles['my-applications-workspace']}>
            {/* HERO / HEADER SECTION */}
            <div className={styles['workspace-hero']}>
                <div className={styles['hero-glow']} />
                <div className={styles.container}>
                     <div className={styles['hero-layout']}>
                         <div className={styles['hero-content']}>
                             <div className={styles['hero-badge']}>
                                 <Sparkles size={12} />
                                 {t('applications.hero.eyebrow')}
                             </div>
                             <div className={styles['title-row']}>
                                 <div className={styles['t-icon']}><Trophy size={20} /></div>
                                 <h1>{t('applications.hero.title')}</h1>
                             </div>
                             <p className={styles.lead}>{t('applications.hero.subtitle')}</p>

                            <div className={styles['stats-strip']}>
                                 <OverviewStat icon={Clock3} label={t('applications.hero.stats.active')} value={stats.active} helper={t('applications.hero.stats.active_help')} />
                                 <OverviewStat icon={CheckCircle2} label={t('applications.hero.stats.completed')} value={stats.completed} helper={t('applications.hero.stats.completed_help')} />
                            </div>
                             {stats.pendingInvites > 0 && (
                                <button
                                  type="button"
                                  className={styles['invite-callout']}
                                  onClick={() => setActiveTab('invitations')}
                                >
                                  <span><Mail size={16} /> {t(`applications.${stats.pendingInvites === 1 ? 'pending_invitation_one' : 'pending_invitation_many'}`, { count: stats.pendingInvites })}</span>
                                  <strong>{t('applications.review_now')}</strong>
                                </button>
                             )}
                         </div>
                         <div className={styles['hero-summary']} aria-label={t('applications.hero.total_label')}>
                             <span>{t('applications.hero.total_label')}</span>
                             <strong>{applications.length}</strong>
                             <small>{t('applications.hero.total_help')}</small>
                         </div>
                     </div>
                </div>
            </div>

            <div className={styles.container}>
                <div className={styles['controls-bar']}>
                    <div className={styles['tabs-pill']}>
                        <button
                           className={clsx(activeTab === 'active' && styles.active)}
                           onClick={() => setActiveTab('active')}
                         >
                             <Clock3 size={14} /> {t('applications.tabs.active')}
                         </button>
                         <button
                           className={clsx(activeTab === 'invitations' && styles.active)}
                           onClick={() => setActiveTab('invitations')}
                         >
                             <Mail size={14} /> {t('applications.tabs.invitations')} {stats.pendingInvites > 0 && <span className={styles['badge-count']}>{stats.pendingInvites}</span>}
                         </button>
                         <button
                           className={clsx(activeTab === 'past' && styles.active)}
                           onClick={() => setActiveTab('past')}
                         >
                             <CheckCircle2 size={14} /> {t('applications.tabs.past')}
                         </button>
                    </div>

                    <div className={styles['search-wrapper']}>
                        <Search size={16} />
                         <input
                           type="text"
                           placeholder={t('applications.filters.search_placeholder')}
                           value={searchTerm}
                           onChange={(e) => setSearchTerm(e.target.value)}
                         />
                    </div>
                </div>

                <motion.div
                  className={styles['apps-grid']}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                    <AnimatePresence mode="popLayout">
                        {activeTab === 'invitations' ? (
                            pendingInvitations.map(inv => (
                                <motion.div
                                  key={inv.id}
                                  layout
                                  initial={{ opacity: 0, scale: 0.9 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  exit={{ opacity: 0, scale: 0.9 }}
                                  transition={{ duration: 0.3 }}
                                >
                                    <InvitationItem inv={inv} onRespond={handleInvitationResponse} />
                                </motion.div>
                            ))
                        ) : (
                            filteredList.map(app => (
                                <motion.div
                                  key={app.id}
                                  layout
                                  initial={{ opacity: 0, scale: 0.9 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  exit={{ opacity: 0, scale: 0.9 }}
                                  transition={{ duration: 0.3 }}
                                >
                                    <ApplicationItem app={app} />
                                </motion.div>
                            ))
                        )}
                    </AnimatePresence>

                    {activeTab === 'invitations' && pendingInvitations.length === 0 && (
                         <div className={styles['empty-st']}>
                             <div className={styles['empty-icon']}><Mail size={40} /></div>
                             <h3>{t('applications.empty.no_invitations')}</h3>
                             <p>{t('applications.empty.no_invitations_help')}</p>
                             <Button variant="primary" onClick={() => navigate('/creator/feed')}>{t('applications.empty.explore_feed')}</Button>
                         </div>
                    )}

                    {activeTab !== 'invitations' && filteredList.length === 0 && (
                         <div className={styles['empty-st']}>
                             <div className={styles['empty-icon']}><Plus size={40} /></div>
                             <h3>{t('applications.empty.no_results')}</h3>
                             <p>{t('applications.empty.no_results_help')}</p>
                             <Button variant="primary" onClick={() => navigate('/creator/feed')}>{t('applications.empty.explore_feed')}</Button>
                         </div>
                    )}
                </motion.div>
            </div>
        </div>
    );
};

export default MyApplications;
